import { createSelector } from '@reduxjs/toolkit';
import { UserState } from './user.types';
import { IUser } from '../../types';

// Define the root state interface
interface RootState {
  user: UserState;
}

// Base selector for user state
const selectUserState = (state: RootState): UserState => state.user;

export const selectCurrentUser = createSelector(
  [selectUserState],
  (userState: UserState): IUser | null => userState.currentUser
);

// Workspace selectors
export const selectWorkspace = createSelector(
  [selectUserState],
  (userState: UserState): any => userState.workspace
);

export const selectWorkspaceId = createSelector(
  [selectWorkspace],
  (workspace: any): string | null => workspace?.workspace?._id || null
);

export const selectWorkspaceName = createSelector(
  [selectWorkspace],
  (workspace: any): string | null => workspace?.workspace?.name || null
);

// Authentication selectors
export const selectIsAuthenticated = createSelector(
  [selectUserState],
  (userState: UserState): boolean => userState.isAuthenticated
);

export const selectAuthToken = createSelector(
  [selectUserState],
  (userState: UserState): string | null => userState.authToken
);

export const selectUserRole = createSelector(
  [selectCurrentUser],
  (user: IUser | null): string | null => user?.role || null
);

export const selectUserEmail = createSelector(
  [selectCurrentUser],
  (user: IUser | null): string | null => user?.email || null
);

export const selectUserName = createSelector(
  [selectCurrentUser],
  (user: IUser | null): string | null => user?.first_name || null
);

export const selectUserId = createSelector(
  [selectCurrentUser],
  (user: IUser | null): string | null => user?._id || null
);

// Loading state selectors
export const selectUserLoading = createSelector(
  [selectUserState],
  (userState: UserState): boolean => userState.loading
);

export const selectUserLoginLoading = createSelector(
  [selectUserState],
  (userState: UserState): boolean => userState.loginLoading
);

export const selectUserRegisterLoading = createSelector(
  [selectUserState],
  (userState: UserState): boolean => userState.registerLoading
);

export const selectUserProfileLoading = createSelector(
  [selectUserState],
  (userState: UserState): boolean => userState.profileLoading
);

export const selectUserUpdateLoading = createSelector(
  [selectUserState],
  (userState: UserState): boolean => userState.updateLoading
);

export const selectUserDataLoading = createSelector(
  [selectUserState],
  (userState: UserState): boolean => userState.userDataLoading
);

export const selectWorkspaceDataLoading = createSelector(
  [selectUserState],
  (userState: UserState): boolean => userState.workspaceDataLoading
);

// Error state selectors
export const selectUserError = createSelector(
  [selectUserState],
  (userState: UserState): string | null => userState.error
);

export const selectUserLoginError = createSelector(
  [selectUserState],
  (userState: UserState): string | null => userState.loginError
);

export const selectUserRegisterError = createSelector(
  [selectUserState],
  (userState: UserState): string | null => userState.registerError
);

export const selectUserProfileError = createSelector(
  [selectUserState],
  (userState: UserState): string | null => userState.profileError
);

export const selectUserUpdateError = createSelector(
  [selectUserState],
  (userState: UserState): string | null => userState.updateError
);

export const selectUserDataError = createSelector(
  [selectUserState],
  (userState: UserState): string | null => userState.userDataError
);

export const selectWorkspaceDataError = createSelector(
  [selectUserState],
  (userState: UserState): string | null => userState.workspaceDataError
);

// Success state selectors
export const selectUserSuccess = createSelector(
  [selectUserState],
  (userState: UserState): string | null => userState.success
);

export const selectUserLoginSuccess = createSelector(
  [selectUserState],
  (userState: UserState): boolean => userState.loginSuccess
);

export const selectUserRegisterSuccess = createSelector(
  [selectUserState],
  (userState: UserState): boolean => userState.registerSuccess
);

export const selectUserUpdateSuccess = createSelector(
  [selectUserState],
  (userState: UserState): boolean => userState.updateSuccess
);

export const selectUserDataSuccess = createSelector(
  [selectUserState],
  (userState: UserState): boolean => userState.userDataSuccess
);

export const selectWorkspaceDataSuccess = createSelector(
  [selectUserState],
  (userState: UserState): boolean => userState.workspaceDataSuccess
);

// Combined selectors
export const selectUserAuthState = createSelector(
  [selectIsAuthenticated, selectCurrentUser, selectAuthToken],
  (isAuthenticated: boolean, user: IUser | null, authToken: string | null) => ({
    isAuthenticated,
    user,
    authToken,
  })
);

export const selectUserLoadingState = createSelector(
  [
    selectUserLoginLoading,
    selectUserRegisterLoading,
    selectUserProfileLoading,
    selectUserUpdateLoading,
    selectUserDataLoading,
    selectWorkspaceDataLoading,
  ],
  (
    loginLoading: boolean,
    registerLoading: boolean,
    profileLoading: boolean,
    updateLoading: boolean,
    userDataLoading: boolean,
    workspaceDataLoading: boolean
  ) => ({
    loginLoading,
    registerLoading,
    profileLoading,
    updateLoading,
    userDataLoading,
    workspaceDataLoading,
    anyLoading:
      loginLoading ||
      registerLoading ||
      profileLoading ||
      updateLoading ||
      userDataLoading ||
      workspaceDataLoading,
  })
);

export const selectUserErrorState = createSelector(
  [
    selectUserLoginError,
    selectUserRegisterError,
    selectUserProfileError,
    selectUserUpdateError,
    selectUserDataError,
    selectWorkspaceDataError,
  ],
  (
    loginError: string | null,
    registerError: string | null,
    profileError: string | null,
    updateError: string | null,
    userDataError: string | null,
    workspaceDataError: string | null
  ) => ({
    loginError,
    registerError,
    profileError,
    updateError,
    userDataError,
    workspaceDataError,
    hasError: !!(
      loginError ||
      registerError ||
      profileError ||
      updateError ||
      userDataError ||
      workspaceDataError
    ),
  })
);
